// frontend/src/components/Header/Header.jsx

import React, { useState, useContext, useEffect } from "react";
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import { LANGUAGES } from '../LanguageSwitcher/LanguageSwitcher';
import "./Header.css"; // Make sure the CSS file is imported
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from "../../contexts/AuthContext";
import { useUnsavedChanges } from "../../contexts/UnsavedChangesContext";
import { UIContext } from "../../contexts/UIContext";
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HistoryIcon from '@mui/icons-material/History';
import ExploreIcon from '@mui/icons-material/Explore';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import BackupTableIcon from '@mui/icons-material/BackupTable';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PaymentsIcon from '@mui/icons-material/Payments';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import CodeIcon from '@mui/icons-material/Code';
import GppGoodIcon from '@mui/icons-material/GppGood';


// Helper to get initials from a name
const getInitials = (name = '', email = '') => {
  if (email.endsWith('@guest.local')) {
    return 'G';
  }
  if (name && name.trim().length > 0) {
    const parts = name.split(' ');
    if (parts.length > 1) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name[0].toUpperCase();
  }
  if (email) {
    return email[0].toUpperCase();
  }
  return 'U'; // User
};

function Header({
  showDataFlowPreview = true,
  hideUserActions = false,
  disableLogoLink = false,
  showBackButton = false,
  showLanguageSwitcher = false,
  backButtonLink = "/app",
  backButtonLabel = null
}) {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, user, logout } = useContext(AuthContext);
  const { isDirty } = useUnsavedChanges();
  const { setIsPageBlurred, openDocViewer } = useContext(UIContext);
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const LogoComponent = disableLogoLink ? 'div' : RouterLink;
  const [langAnchorEl, setLangAnchorEl] = useState(null);
  const isLangMenuOpen = Boolean(langAnchorEl);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const [isUnsavedDialogOpen, setIsUnsavedDialogOpen] = useState(false);
  const [nextLocation, setNextLocation] = useState(null);

  // This effect links the dialog's visibility to the global blur state.
  useEffect(() => {
    setIsPageBlurred(isUnsavedDialogOpen);

    // Cleanup function: if the Header unmounts for any reason while the
    // dialog is open, make sure to remove the blur.
    return () => setIsPageBlurred(false);
  }, [isUnsavedDialogOpen, setIsPageBlurred]);

  useEffect(() => {
    // We search for any of these scrollable containers
    const scrollableSelectors = [
      '.app-main-container',
      '.landing-page-container',
      '.app-wrapper'
    ];

    const setupScrollListener = () => {
      const scrollableElement = document.querySelector(scrollableSelectors.join(', '));

      if (!scrollableElement) {
        requestAnimationFrame(setupScrollListener);
        return;
      }

      const handleScroll = () => {
        setIsScrolled(scrollableElement.scrollTop > 10);
      };

      handleScroll(); // Check on mount
      scrollableElement.addEventListener('scroll', handleScroll);

      return () => {
        scrollableElement.removeEventListener('scroll', handleScroll);
      };
    };

    const cleanup = setupScrollListener();
    return cleanup;
  }, [location.pathname]); // Re-run when page changes

  const handleProtectedLinkClick = (event, path) => {
    if (isDirty) {
      event.preventDefault(); // Stop the <Link> from navigating immediately
      setNextLocation(path); // Remember where we want to go
      setIsUnsavedDialogOpen(true); // Open our dialog
    }
    // If not dirty, the click proceeds as normal.
    handleMenuClose(); // Close the avatar menu regardless
  };

  const handleLeavePage = () => {
    setIsUnsavedDialogOpen(false);
    if (nextLocation) {
      navigate(nextLocation); // Navigate to the stored path
    }
  };

  const handleStayOnPage = () => {
    setIsUnsavedDialogOpen(false);
    setNextLocation(null);
    // Dispatch an event so the active page knows to scroll to the unsaved area
    window.dispatchEvent(new Event('scrollToUnsavedChanges'));
  };

  const handleLangMenuClick = (event) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLangMenuClose = () => {
    setLangAnchorEl(null);
  };

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    handleLangMenuClose();
    handleMenuClose(); // Also close the main user menu
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
  };

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const list = () => (
    <Box
      sx={{ width: 250 }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <List>
        <ListItem key={"data-flow"} disablePadding>
          <ListItemButton component="a" href="https://lucid.app/lucidchart/17a60671-1716-4b15-9816-c1bb63a75d45/view" target="_blank" rel="noopener noreferrer">
            <ListItemText primary={"Data Flow"} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  const displayName = user && user.email.endsWith('@guest.local')
    ? t('header.userMenu.guestName')
    : user?.full_name || user?.email;

  const currentLanguage = LANGUAGES.find(lang => i18n.language.startsWith(lang.code)) || LANGUAGES[0];
  const appBarClassName = `universal-nav-appbar ${isScrolled ? 'scrolled' : ''}`;

  return (
    <>
      <AppBar
        position="sticky"
        className={appBarClassName}
        sx={{ top: 0, zIndex: 1100 }}
      >
        <Toolbar className="header-toolbar">
          {showBackButton ? (
            <Button
              component={RouterLink}
              to={backButtonLink}
              variant="text"
              startIcon={<ArrowBackIcon />}
              // Add a class for more specific CSS targeting
              className="header-back-button"
              onClick={(e) => handleProtectedLinkClick(e, backButtonLink)}
            >
              <span className="back-button-text">
                {/* USE DYNAMIC LABEL OR FALLBACK TO TRANSLATION */}
                {backButtonLabel || t('header.backToAnalyzer')} 
              </span>
            </Button>
          ) : (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="logo"
              className="header-logo-button"
              disableRipple
              component={LogoComponent}
              to={disableLogoLink ? undefined : "/"}
            >
              <img src="/static/vetraai_logo_grey.png" alt="Vetra AI Logo" className="logo" />
            </IconButton>
          )}

          <Typography component="div" className="header-title-container">
            {/* FIX FOR COLOR-CONTRAST: Use inline style to force higher contrast color */}
            <Typography variant="h5" component="h1" className="header-title" sx={{ color: '#ffffff !important' }}>
             Vetra AI
            </Typography>
            <Typography variant="caption" component="p" className="header-tagline" sx={{ color: 'rgba(255, 255, 255, 0.95) !important' }}>
              {t('header.tagline')}
            </Typography>
          </Typography>

          <Box className="header-actions-container">
            {isAuthenticated && user && !hideUserActions ? (
              <>
                <IconButton onClick={handleMenuOpen} className="user-avatar-button">
                  <Avatar className="user-avatar">
                    {getInitials(user.full_name, user.email)}
                  </Avatar>
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  keepMounted
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  sx={{ zIndex: 1360 }}
                  slotProps={{ paper: { className: 'user-menu' } }}
                >
                  <MenuItem dense disableRipple className="user-menu-info-item">
                    <Button
                      className="user-menu-lang-switcher"
                      onClick={handleLangMenuClick}
                      aria-label="Change Language"
                      aria-controls={isLangMenuOpen ? 'language-menu-app' : undefined}
                      aria-haspopup="true"
                      aria-expanded={isLangMenuOpen ? 'true' : undefined}
                    >
                      <img src={`/static/flags/${currentLanguage.flag}`} alt={`${currentLanguage.name} flag`} className="flag-icon-menu" />
                      <ArrowDropDownIcon />
                    </Button>
                    <Box>
                      <Typography variant="caption" className="user-menu-label">{t('header.userMenu.userLabel')}</Typography>
                      <Typography variant="body2" className="user-menu-name">
                        {displayName}
                      </Typography>
                    </Box>
                  </MenuItem>

                  <Divider sx={{ my: 0.5 }} />

                  {/* --- GROUP 1: ADMIN & BILLING --- */}

                  {!user.email.endsWith('@guest.local') && (
                    <MenuItem component={RouterLink} to="/app/account" onClick={(e) => handleProtectedLinkClick(e, '/app/account')} className="user-menu-link-item">
                      <AccountCircleOutlinedIcon />
                      <ListItemText primary={t('header.userMenu.myAccount')} />
                    </MenuItem>
                  )}

                  {!user.email.endsWith('@guest.local') && (
                    <MenuItem component={RouterLink} to="/app/billing" onClick={(e) => handleProtectedLinkClick(e, '/app/billing')} className="user-menu-link-item">
                      <PaymentsIcon />
                      <ListItemText primary={t('header.userMenu.billing')} />
                    </MenuItem>
                  )}

                  {!user.email.endsWith('@guest.local') && (
                      <Divider sx={{ my: 0.5 }} />
                  )}

                  {/* --- GROUP 2: ANALYSIS RUNNERS --- */}

                  {!user.email.endsWith('@guest.local') && (
                    <MenuItem component={RouterLink} to="/app/bulk-analysis" onClick={(e) => handleProtectedLinkClick(e, '/app/bulk-analysis')} className="user-menu-link-item">
                      <BackupTableIcon />
                      <ListItemText primary={t('header.userMenu.bulkAnalysis')} />
                    </MenuItem>
                  )}

                  {!user.email.endsWith('@guest.local') && (
                    <MenuItem 
                      onClick={() => { openDocViewer('api/api_reference.md'); handleMenuClose(); }} 
                      className="user-menu-link-item"
                    >
                      <CodeIcon />
                      <ListItemText primary={t('header.userMenu.apiReference')} />
                    </MenuItem>
                  )}

                  {!user.email.endsWith('@guest.local') && (
                      <Divider sx={{ my: 0.5 }} />
                  )}

                  {/* --- GROUP 3: ANALYSIS MANAGEMENT --- */}

                  {!user.email.endsWith('@guest.local') && (
                    <MenuItem component={RouterLink} to="/app/history" onClick={(e) => handleProtectedLinkClick(e, '/app/history')} className="user-menu-link-item">
                      <HistoryIcon />
                      <ListItemText primary={t('header.userMenu.myHistory')} />
                    </MenuItem>
                  )}

                  {!user.email.endsWith('@guest.local') && (
                    <MenuItem component={RouterLink} to="/app/licenses" onClick={(e) => handleProtectedLinkClick(e, '/app/licenses')} className="user-menu-link-item">
                      <CardMembershipIcon />
                      <ListItemText primary={t('header.userMenu.myLicenses', 'My Licenses')} />
                    </MenuItem>
                  )}

                  {!user.email.endsWith('@guest.local') && (
                    <MenuItem component={RouterLink} to="/app/threat-radar" onClick={(e) => handleProtectedLinkClick(e, '/app/threat-radar')} className="user-menu-link-item">
                      <GppGoodIcon />
                      <ListItemText primary={t('header.userMenu.threatRadar', 'Threat Radar')} />
                    </MenuItem>
                  )}
                  
                  {!user.email.endsWith('@guest.local') && (
                      <Divider sx={{ my: 0.5 }} />
                  )}

                  {/* --- GROUP 4: HELP & INFO --- */}

                  <MenuItem
                    onClick={() => { openDocViewer(); handleMenuClose(); }}
                    className="user-menu-link-item"
                  >
                    <LibraryBooksIcon />
                    <ListItemText primary={t('header.userMenu.browseDocs')} />
                  </MenuItem>

                  <MenuItem component={RouterLink} to="/" onClick={(e) => handleProtectedLinkClick(e, '/')} className="user-menu-link-item user-menu-explore-item">
                    <ExploreIcon />
                    <ListItemText primary={t('header.userMenu.productOverview')} />
                  </MenuItem>
                  <Divider sx={{ my: 0.5 }} />
                  <MenuItem dense className="user-menu-logout-item">
                    <Button
                      variant="contained"
                      onClick={handleLogout}
                      className="logout-button"
                    >
                      {t('header.userMenu.logout')}
                    </Button>
                  </MenuItem>
                </Menu>

                <Menu
                  id="language-menu-app"
                  anchorEl={langAnchorEl}
                  open={isLangMenuOpen}
                  onClose={handleLangMenuClose}
                  MenuListProps={{ 'aria-labelledby': 'language-button' }}
                  PaperProps={{ className: 'language-menu-paper' }}
                  sx={{ zIndex: 1360 }}
                >
                  {LANGUAGES.map((lang) => (
                    <MenuItem
                      key={lang.code}
                      selected={i18n.language.startsWith(lang.code)}
                      onClick={() => handleLanguageChange(lang.code)}
                      className="language-menu-item"
                    >
                      {/* Re-use the nice layout from LanguageSwitcher.jsx */}
                      <Box className="menu-item-content">
                        <img src={`/static/flags/${lang.flag}`} alt={`${lang.name} flag`} className="flag-icon" />
                        <Typography variant="body1" className="language-name">
                          {lang.name.toUpperCase()}
                        </Typography>
                        {/* 6. ADDED: Checkmark for the selected language */}
                        {i18n.language.startsWith(lang.code) && (
                          <CheckIcon className="selected-check-icon" />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Menu>

              </>
            ) : showLanguageSwitcher ? (
              <LanguageSwitcher />
            ) : null}
          </Box>
        </Toolbar>
      </AppBar>

      {showDataFlowPreview && (
        <section className="data-flow-preview" aria-labelledby="intro-hook-title">
          <Typography variant="h6" component="h2" className="intro-hook" id="intro-hook-title">
            {t('header.introHook')}
          </Typography>
          <div className="intro-description-container">
            <Typography paragraph className="intro-description">
              {t('header.introDescription1')}
            </Typography>
            <Typography paragraph className="intro-description">
              {t('header.introDescription2')}
            </Typography>
          </div>
        </section>
      )}

      <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
        {list()}
      </Drawer>

      {ReactDOM.createPortal(
        <div className={`confirmation-dialog-overlay ${isUnsavedDialogOpen ? 'open' : ''}`} onClick={handleStayOnPage}>
          {isUnsavedDialogOpen && (
            <div className="confirmation-dialog-content" onClick={(e) => e.stopPropagation()}>
              <IconButton aria-label="close" onClick={handleStayOnPage} className="dialog-close-button"><CloseIcon /></IconButton>
              <h2 className="confirmation-dialog-title">{t('bulkAnalysisPage.dialogs.unsavedChanges.title')}</h2>
              <div className="confirmation-dialog-body">
                <p className="confirmation-dialog-text">{t('bulkAnalysisPage.dialogs.unsavedChanges.description')}</p>
              </div>
              <div className="confirmation-dialog-actions">
                <Button onClick={handleStayOnPage} variant="outlined">
                  {t('bulkAnalysisPage.dialogs.unsavedChanges.cancelButton')}
                </Button>
                <Button onClick={handleLeavePage} color="error" variant="contained">
                  {t('bulkAnalysisPage.dialogs.unsavedChanges.confirmButton')}
                </Button>
              </div>
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}

export default Header;